import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ArrowLeft, Wrench, Calendar, Gauge, Clock } from 'lucide-react';
import { VehicleService, VehicleServiceStatus } from '../../../../shared/types/vehicle-service.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import useSettingLists from '../../wrappers/use-setting-list-item';
import useVehicleService from '../../wrappers/use-vehicle-service';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import DocumentList from '../../components/DocumentList';
import {
    serviceDetailStyles,
    serviceDetailIconColor,
} from './ServiceDetail.styles';

const STATUS_CHIP: Record<VehicleServiceStatus, { color: 'default' | 'primary' | 'warning' | 'success' | 'error' }> = {
    [VehicleServiceStatus.Ordered]:         { color: 'primary' },
    [VehicleServiceStatus.InProgress]:      { color: 'warning' },
    [VehicleServiceStatus.WaitingForParts]: { color: 'warning' },
    [VehicleServiceStatus.Completed]:       { color: 'success' },
    [VehicleServiceStatus.Cancelled]:       { color: 'error' },
};

const ServiceDetail: React.FC = () => {
    const { vin, serviceId } = useParams<{ vin: string; serviceId: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const settingLists = useSettingLists();
    const { get: getService } = useVehicleService();
    const [service, setService] = useState<VehicleService | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});

    useEffect(() => {
        settingLists.list('serviceTypes').then((items) => {
            setServiceTypeMap(Object.fromEntries(items.map((i: SettingListItem) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!serviceId) return;
        const fetch = async () => {
            const loaded = await getService(serviceId);
            if (!loaded) {
                setNotFound(true);
            } else {
                setService(loaded);
            }
            setLoading(false);
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceId]);

    if (loading) {
        return (
            <Box sx={serviceDetailStyles.loadingContainer}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !service) {
        return <Alert severity="error">{t('serviceDetail.notFound')}</Alert>;
    }

    const serviceTypeLabel = serviceTypeMap[service.serviceType] ?? service.serviceType;
    const statusLabel = t(`vehicleServiceStatus.${service.status}`);
    const statusColor = STATUS_CHIP[service.status]?.color ?? 'default';

    return (
        <Box sx={serviceDetailStyles.root}>
            <Box sx={serviceDetailStyles.header}>
                <IconButton component={Link} to={`/app/vehicles/${vin}`} size="small">
                    <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="h4" fontWeight={700}>{t('serviceDetail.title')}</Typography>
                <Chip label={serviceTypeLabel} size="small" icon={<Wrench size={14} />} />
                <Chip label={statusLabel} size="small" color={statusColor} />
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={serviceDetailStyles.card}>
                        <Box sx={serviceDetailStyles.cardHeader}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('serviceDetail.infoTitle')}</Typography>
                        </Box>
                        {[
                            { label: t('serviceDetail.serviceTypeLabel'), value: serviceTypeLabel },
                            { label: t('serviceDetail.recordDate'), value: formatDate(service.createdAt) },
                            { label: t('serviceDetail.preferredDate'), value: formatDate(service.preferredDate) },
                        ].map((row, i, arr) => (
                            <Box key={row.label}>
                                <Box sx={serviceDetailStyles.infoRow}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>{row.label}</Typography>
                                    <Typography variant="body2">{row.value}</Typography>
                                </Box>
                                {i < arr.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={serviceDetailStyles.card}>
                        <Box sx={serviceDetailStyles.cardHeader}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('serviceDetail.machineStatusTitle')}</Typography>
                        </Box>
                        {[
                            {
                                label: t('serviceDetail.kmLabel'),
                                icon: <Gauge size={16} color={serviceDetailIconColor} />,
                                value: service.currentMileage !== undefined ? `${service.currentMileage} km` : '—',
                            },
                            {
                                label: t('serviceDetail.mthLabel'),
                                icon: <Clock size={16} color={serviceDetailIconColor} />,
                                value: service.currentHours !== undefined ? `${service.currentHours} h` : '—',
                            },
                            {
                                label: t('serviceDetail.vinLabel'),
                                icon: <Calendar size={16} color={serviceDetailIconColor} />,
                                value: service.vehicleUid,
                                mono: true,
                            },
                        ].map((row, i, arr) => (
                            <Box key={row.label}>
                                <Box sx={serviceDetailStyles.statusRow}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>{row.label}</Typography>
                                    <Box sx={serviceDetailStyles.statusValueWrap}>
                                        {row.icon}
                                        <Typography variant="body2" fontFamily={row.mono ? 'monospace' : undefined}>{row.value}</Typography>
                                    </Box>
                                </Box>
                                {i < arr.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={serviceDetailStyles.card}>
                <Box sx={serviceDetailStyles.cardHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('serviceDetail.descriptionTitle')}</Typography>
                </Box>
                <Box sx={serviceDetailStyles.descriptionContent}>
                    <Typography variant="body2" color={service.description ? 'text.primary' : 'text.secondary'}>
                        {service.description || t('serviceDetail.noDescription')}
                    </Typography>
                </Box>
            </Paper>

            <Paper elevation={0} sx={serviceDetailStyles.card}>
                <DocumentList
                    sectionTitle={t('serviceDetail.documentsTitle')}
                    filters={{ vehicleServiceUid: serviceId, garageUid: user?.garageUid }}
                    uploadData={{ vehicleUid: vin, vehicleServiceUid: serviceId, garageUid: user?.garageUid, dealerId: service?.dealerId }}
                />
            </Paper>

            {service.status === VehicleServiceStatus.Completed && (
                <Paper elevation={0} sx={serviceDetailStyles.card}>
                    <Box sx={serviceDetailStyles.cardHeader}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('serviceDetail.completedTitle')}</Typography>
                    </Box>
                    <Box sx={serviceDetailStyles.completedContent}>

                        <Box sx={serviceDetailStyles.completedRow}>
                            <Typography variant="body2" fontWeight={500} sx={serviceDetailStyles.completedLabel}>{t('serviceDetail.plannedFinish')}</Typography>
                            <Typography variant="body2">
                                {service.plannedFinish ? formatDate(service.plannedFinish) : '—'}
                            </Typography>
                        </Box>

                        <Box sx={serviceDetailStyles.completedRow}>
                            <Typography variant="body2" fontWeight={500} sx={serviceDetailStyles.completedLabel}>{t('serviceDetail.completedAt')}</Typography>
                            <Typography variant="body2">
                                {service.finishedAt ? formatDate(service.finishedAt) : '—'}
                            </Typography>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="body2" fontWeight={500} mb={1.5}>{t('serviceDetail.tasksTitle')}</Typography>
                            {service.serviceTasks && service.serviceTasks.length > 0 ? (
                                <List dense disablePadding sx={serviceDetailStyles.tasksList}>
                                    {service.serviceTasks.map((task, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && <Divider />}
                                            <ListItem sx={serviceDetailStyles.taskItem}>
                                                <ListItemText primary={task} primaryTypographyProps={{ variant: 'body2' }} />
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary">{t('serviceDetail.noTasks')}</Typography>
                            )}
                        </Box>

                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default ServiceDetail;
